import React, { useState, useCallback, useEffect } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import type { TenantThemeConfig } from '../types/theme'
import { normalizeThaiMobile } from '../services/registrationService'
import {
  persistModernRegisterCompleteFlowState,
  type ModernRegisterCompleteFlowState,
} from '@huay/shared/registerFlowState'
import './HtdAuth.css'

const PIN_LENGTH = 6
const DIGITS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0]

type PasscodeState = { fromRegistration?: boolean; phone?: string; sessionId?: string; username?: string }

/** หน้า Passcode — ใช้เฉพาะ flow สมัครสมาชิกเก่าเท่านั้น; flow login ถูกย้ายไปหน้า /login แล้ว */
interface ModernPasscodePageProps {
  themeConfig?: TenantThemeConfig | null
}

const ModernPasscodePage: React.FC<ModernPasscodePageProps> = ({ themeConfig }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const state = (location.state ?? {}) as PasscodeState
  const fromRegistration = state?.fromRegistration === true
  const regPhone = state?.phone ?? ''
  const regSessionId = state?.sessionId ?? ''
  const regUsername = state?.username ?? ''

  // โหมดสมัคร: ใส่ 2 ครั้ง
  const [regStep, setRegStep] = useState<'first' | 'confirm'>('first')
  const [pinFirst, setPinFirst] = useState<string[]>(Array(PIN_LENGTH).fill(''))
  const [pinConfirm, setPinConfirm] = useState<string[]>(Array(PIN_LENGTH).fill(''))
  const [regSubmitting, setRegSubmitting] = useState(false)

  useEffect(() => {
    if (!fromRegistration) return
    document.body.classList.add('htd-auth-active')
    return () => document.body.classList.remove('htd-auth-active')
  }, [fromRegistration])

  useEffect(() => {
    if (!themeConfig) return
    if (fromRegistration) {
      if (!regPhone || !regSessionId) {
        navigate('/register', { replace: true })
      } else {
        const completeState: ModernRegisterCompleteFlowState = {
          fromOtp: true,
          phone: normalizeThaiMobile(regPhone),
          sessionId: regSessionId,
          ...(regUsername && regUsername.trim() ? { username: regUsername.trim() } : {}),
        }
        persistModernRegisterCompleteFlowState(completeState)
        navigate('/register/complete', {
          replace: true,
          state: completeState,
        })
      }
    }
  }, [fromRegistration, regPhone, regSessionId, regUsername, navigate, themeConfig])

  const addDigit = useCallback(
    (d: number, target: 'first' | 'confirm') => {
      if (target === 'first') {
        if (regSubmitting) return
        setPinFirst((prev) => {
          const i = prev.findIndex((c) => c === '')
          if (i === -1) return prev
          const next = [...prev]
          next[i] = String(d)
          return next
        })
      } else {
        if (regSubmitting) return
        setPinConfirm((prev) => {
          const i = prev.findIndex((c) => c === '')
          if (i === -1) return prev
          const next = [...prev]
          next[i] = String(d)
          return next
        })
      }
    },
    [regSubmitting]
  )

  const backspace = useCallback(
    (target: 'first' | 'confirm') => {
      if (target === 'first') {
        if (regSubmitting) return
        setPinFirst((prev) => {
          const i = prev.map((c) => c !== '').lastIndexOf(true)
          if (i === -1) return prev
          const next = [...prev]
          next[i] = ''
          return next
        })
      } else {
        if (regSubmitting) return
        setPinConfirm((prev) => {
          const i = prev.map((c) => c !== '').lastIndexOf(true)
          if (i === -1) return prev
          const next = [...prev]
          next[i] = ''
          return next
        })
      }
    },
    [regSubmitting]
  )

  const handleRegNext = useCallback(() => {
    if (pinFirst.every((c) => c !== '')) setRegStep('confirm')
  }, [pinFirst])

  const handleRegSubmit = useCallback(async () => {
    const p1 = pinConfirm.join('')
    const p2 = pinFirst.join('')
    if (p1.length !== PIN_LENGTH || p2.length !== PIN_LENGTH) return
    if (p1 !== p2) {
      await Swal.fire({
        icon: 'warning',
        title: 'รหัสไม่ตรงกัน',
        text: 'กรุณาใส่รหัสผ่านให้ตรงกันทั้ง 2 ครั้ง',
      })
      setPinConfirm(Array(PIN_LENGTH).fill(''))
      return
    }
    if (!themeConfig?.tenantId) return
    setRegSubmitting(true)
    try {
      // หลังยืนยัน passcode แล้ว ไปหน้า complete (กรอกชื่อ-นามสกุล, ธนาคาร, บัญชี)
      const normPhone = normalizeThaiMobile(regPhone)
      const completeState: ModernRegisterCompleteFlowState = {
        fromPasscode: true,
        phone: normPhone,
        sessionId: regSessionId,
        password: p1,
        ...(regUsername && regUsername.trim() ? { username: regUsername.trim() } : {}),
      }
      persistModernRegisterCompleteFlowState(completeState)
      navigate('/register/complete', {
        replace: true,
        state: completeState,
      })
    } catch (e) {
      await Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: (e as Error)?.message ?? 'กรุณาลองใหม่อีกครั้ง',
      })
      setPinConfirm(Array(PIN_LENGTH).fill(''))
    } finally {
      setRegSubmitting(false)
    }
  }, [pinFirst, pinConfirm, themeConfig?.tenantId, regPhone, regSessionId, regUsername, navigate])

  const isRegFirstFull = pinFirst.every((c) => c !== '')
  const isRegConfirmFull = pinConfirm.every((c) => c !== '')

  if (fromRegistration) {
    const target: 'first' | 'confirm' = regStep === 'first' ? 'first' : 'confirm'
    const activePin = regStep === 'first' ? pinFirst : pinConfirm
    return (
      <div className="htd-auth">
        <div className="htd-auth__ambient" aria-hidden>
          <span className="htd-auth__orb htd-auth__orb--a" />
          <span className="htd-auth__orb htd-auth__orb--b" />
        </div>

        <div className="htd-auth__shell">
          <div className="htd-auth__card">
            <div className="htd-auth__head">
              <span className="htd-auth__step">STEP 2</span>
              <h1 className="htd-auth__title">ตั้งรหัสผ่าน (PIN)</h1>
              <p className="htd-auth__sub">
                {regStep === 'first'
                  ? 'ตั้งรหัสผ่านตัวเลขอย่างน้อย 6 หลัก'
                  : 'ใส่รหัสผ่านอีกครั้งเพื่อยืนยัน'}
              </p>
            </div>

            {regStep === 'confirm' && regSubmitting && (
              <p className="htd-auth__hint htd-auth__hint--info">กำลังดำเนินการ...</p>
            )}

            <div className="htd-auth__pin">
              {activePin.map((char, i) => (
                <div key={i} className={`htd-auth__pin-cell${char ? ' htd-auth__pin-cell--filled' : ''}`}>
                  {char ? '•' : ''}
                </div>
              ))}
            </div>

            <div className="htd-auth__keys">
              {DIGITS.slice(0, 9).map((n) => (
                <button
                  key={n}
                  type="button"
                  className="htd-auth__key"
                  onClick={() => addDigit(n, target)}
                >
                  {n}
                </button>
              ))}
              <span className="htd-auth__key htd-auth__key--empty" aria-hidden />
              <button type="button" className="htd-auth__key" onClick={() => addDigit(0, target)}>
                0
              </button>
              <button
                type="button"
                className="htd-auth__key htd-auth__key--accent"
                onClick={() => backspace(target)}
                aria-label="ลบ"
              >
                <i className="fas fa-backspace" />
              </button>
            </div>

            {regStep === 'first' && (
              <button
                type="button"
                className="htd-auth__submit"
                onClick={handleRegNext}
                disabled={!isRegFirstFull}
              >
                ถัดไป
              </button>
            )}

            {regStep === 'confirm' && (
              <div className="htd-auth__actions">
                <button
                  type="button"
                  className="htd-auth__ghost"
                  onClick={() => {
                    setRegStep('first')
                    setPinConfirm(Array(PIN_LENGTH).fill(''))
                  }}
                  disabled={regSubmitting}
                >
                  ย้อนกลับ
                </button>
                <button
                  type="button"
                  className="htd-auth__submit"
                  onClick={handleRegSubmit}
                  disabled={!isRegConfirmFull || regSubmitting}
                >
                  {regSubmitting ? 'กำลังดำเนินการ...' : 'ยืนยัน'}
                </button>
              </div>
            )}

            <Link to="/register" className="htd-auth__back">
              <i className="fas fa-chevron-left" aria-hidden />
              กลับไปสมัครสมาชิก
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return <Navigate to="/login" replace />
}

export default ModernPasscodePage
